// frontend/src/components/FunnelHero.tsx
// Большая горизонтальная воронка из 7 ступеней покупки + CPA + CR.
// SVG-трапеции, показывают сужение от просмотра списка до доставки.
import { METRICS, type MetricId } from "../charts/METRICS.js";
import { formatInt } from "../charts/format.js";

export interface FunnelHeroStep {
  id: MetricId;
  reach: number | null;
  /** ID stage-specific cost-per-action. Например cp_basket для basket. */
  cpaId?: MetricId;
  cpa?: number | null;
}

export interface FunnelHeroProps {
  steps: FunnelHeroStep[];
  /** Заголовок — короткая фраза, например «8 целей Я.Метрики · стоимости и переходы между ступенями». */
  subtitle?: string;
}

const STAGE_COLORS = [
  "var(--series-1)",
  "var(--series-1)",
  "var(--series-2)",
  "var(--series-3)",
  "var(--series-4)",
  "var(--series-5)",
  "var(--series-5)",
];

export function FunnelHero({ steps, subtitle }: FunnelHeroProps) {
  const reaches = steps.map((s) => s.reach ?? 0);
  const max = Math.max(...reaches, 1);

  // bottleneck = шаг с самым большим drop% к следующему
  const drops = steps.map((s, i) => {
    if (i === steps.length - 1) return null;
    const a = s.reach ?? 0;
    const b = steps[i + 1].reach ?? 0;
    if (a <= 0) return null;
    return (b - a) / a; // негативный = drop
  });
  const bottleneckIdx = drops.reduce<{ idx: number; v: number }>(
    (acc, d, i) => {
      if (d == null) return acc;
      if (d < acc.v) return { idx: i, v: d };
      return acc;
    },
    { idx: -1, v: 0 },
  );

  return (
    <div
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-default)",
        borderRadius: 12,
        padding: "28px 32px",
        boxShadow: "var(--shadow-card)",
        marginBottom: 24,
      }}
    >
      <h2
        style={{
          fontSize: 13,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "var(--text-secondary)",
          fontWeight: 600,
          margin: "0 0 4px",
        }}
      >
        Воронка покупки
      </h2>
      <div style={{ color: "var(--text-muted)", fontSize: 12, marginBottom: 24 }}>
        {subtitle ?? "Ступени Я.Метрики · стоимости и переходы между ступенями"}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          justifyContent: "space-between",
          gap: 0,
        }}
      >
        {steps.map((s, i) => {
          const reach = s.reach ?? 0;
          const ratio = reach / max; // 0..1
          // Чтобы трапеция «сужалась» — top-y растёт по убыванию ratio.
          const topY = Math.max(0, Math.min(95, (1 - ratio) * 95));
          const color = STAGE_COLORS[i] ?? "var(--series-3)";
          const meta = METRICS[s.id];
          const cpaMeta = s.cpaId ? METRICS[s.cpaId] : null;
          const cpaText = cpaMeta && s.cpa != null ? `${cpaLabel(s.cpaId!)} ${cpaMeta.format(s.cpa)}` : null;

          // CR к следующему шагу
          const drop = drops[i];
          const dropTxt = drop == null ? null : `${(drop * 100).toFixed(0).replace(".", ",")}%`;
          const dropBad = drop != null && drop <= -0.5;

          return (
            <div
              key={s.id}
              style={{
                flex: 1,
                padding: "0 6px",
                textAlign: "center",
                position: "relative",
              }}
            >
              <div
                style={{
                  height: 110,
                  position: "relative",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                }}
              >
                <svg
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  style={{ width: "100%", height: "100%" }}
                >
                  <polygon
                    points={`0,${topY} 100,${topY} 100,100 0,100`}
                    fill={color}
                    opacity={0.85}
                  />
                </svg>
                {drop != null && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "30%",
                      right: -16,
                      transform: "translateX(50%)",
                      background: dropBad ? "var(--status-bad-bg, #FEE2E2)" : "var(--surface-card)",
                      border: dropBad
                        ? "1px solid #FCA5A5"
                        : "1px solid var(--border-default)",
                      borderRadius: 12,
                      padding: "2px 6px",
                      fontSize: 10,
                      fontWeight: 600,
                      color: dropBad ? "var(--status-bad-fg)" : "var(--text-secondary)",
                      zIndex: 2,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {dropTxt}
                  </div>
                )}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--text-secondary)",
                  marginTop: 8,
                  minHeight: 32,
                }}
              >
                {meta.label}
              </div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: "var(--text-primary)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatInt(reach)}
              </div>
              {cpaText && (
                <div
                  style={{
                    display: "inline-block",
                    padding: "2px 6px",
                    background: "var(--status-bad-bg, #FEE2E2)",
                    color: "var(--status-bad-fg, #991B1B)",
                    borderRadius: 4,
                    fontSize: 10,
                    fontWeight: 600,
                    marginTop: 6,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {cpaText}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {bottleneckIdx.idx >= 0 && bottleneckIdx.v <= -0.3 && (
        <div style={{ marginTop: 16, fontSize: 12, color: "var(--text-muted)" }}>
          💡 <strong>Бутылочное горлышко:</strong>{" "}
          «{METRICS[steps[bottleneckIdx.idx].id].label} → {METRICS[steps[bottleneckIdx.idx + 1].id].label}» теряет{" "}
          {Math.abs(bottleneckIdx.v * 100).toFixed(0)}%.
        </div>
      )}
    </div>
  );
}

function cpaLabel(id: MetricId): string {
  // короткие префиксы для подписи в pill: «CPM 50 ₽», «CP 467 ₽» и т.д.
  switch (id) {
    case "cpm":
      return "CPM";
    case "cpc":
      return "CPC";
    case "cpv":
      return "CPV";
    case "cp_order":
      return "CPA";
    default:
      return "CP";
  }
}
